import React from 'react';
import { Box, Typography, IconButton, Tooltip, Alert } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';

import { neutral } from '_lex/_foundations/phxColors';
import { TemplateSection, TemplateField, DragData, Template } from '../../_types';
import { FieldTypeEnum } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { useField } from 'views/App/Portal/PerformanceReporting/FieldBank/_data_sources/useField';
import { isSectionLockedByParent } from '../../_utils/sectionComparison';
import { isFieldLockedByParent } from '../../_utils/fieldComparison';
import { translate as t } from 'hooks/useTranslations';
import { formatFieldValueForDisplay } from 'views/App/Portal/PerformanceReporting/_utils/fieldInputHelpers';
import { useTemplateBuilderContext, getFieldUniqueId } from '../context';

// Helper function to format field values based on their type
const formatFieldValue = (field: TemplateField, fieldType?: FieldTypeEnum) => {
  const value = field.effectiveDefaultValue;
  const type = fieldType || field.fieldType;

  if (!value || value.trim() === '') {
    return `Enter ${field.effectiveLabel?.toLowerCase() || 'field label'}`;
  }

  return formatFieldValueForDisplay(value, type);
};

interface SortableFieldItemProps {
  field: TemplateField;
  sectionId: number;
  section: TemplateSection;
  parentTemplate?: Template | null;
  onRemove: (sectionId: number, fieldId: number) => void;
  onSetSelectedField: (field: TemplateField) => void;
  openFieldOptionsId: string | null;
  setOpenFieldOptionsId: (fieldId: string | null) => void;
  isInactive?: boolean;
  isDuplicate?: boolean;
  isRemovableChildField?: boolean;
}

const SortableFieldItem: React.FC<SortableFieldItemProps> = ({
  field,
  sectionId,
  section,
  parentTemplate,
  onRemove,
  onSetSelectedField,
  openFieldOptionsId,
  setOpenFieldOptionsId,
  isInactive = false,
  isDuplicate = false,
  isRemovableChildField = false,
}) => {
  // Use centralized state instead of local state
  // Use unique ID (combination of id and templateSectionId for unsaved fields) to avoid conflicts with duplicate fields
  const fieldUniqueId = getFieldUniqueId(field);
  const isSelected = openFieldOptionsId === fieldUniqueId;

  const { isLexipolTemplate } = useTemplateBuilderContext();
  const templateMode = isLexipolTemplate ? 'lexipol' : 'agency';

  // Check if section is locked by parent
  const isSectionLocked = isSectionLockedByParent(section, parentTemplate);

  // Check if field is locked by parent
  const isFieldLocked = isFieldLockedByParent(field, section, parentTemplate);

  // Can remove if section not locked OR field is a removable child field OR field has validation issues (duplicates/inactive must be removed before publish)
  const canRemove = !isSectionLocked || isRemovableChildField || isDuplicate || isInactive;

  // Fetch field data from FieldBank if fieldType is missing
  const { data: fieldBankData } = useField(
    field.fieldId,
    true, // expandCategory
    {
      enabled: !field.fieldType && !!field.fieldId,
    }
  );

  // Use fieldType from template field, or fallback to FieldBank data
  const fieldType = field.fieldType || fieldBankData?.fieldType;

  const dragData: DragData = {
    type: 'field-within-section',
    templateField: field,
    sourceSectionId: sectionId,
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id === 0 ? `temp-field-${field.fieldId}` : String(field.id), // Use hybrid ID for uniqueness
    data: dragData,
  });

  const style: React.CSSProperties = isDragging
    ? {
        opacity: 0,
        pointerEvents: 'none',
      }
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      };

  const handleRemove = () => {
    onRemove(sectionId, field.id);
  };

  const handleFieldClick = () => {
    // Toggle field options panel - if this field is selected, close it; otherwise open it
    setOpenFieldOptionsId(isSelected ? null : fieldUniqueId);
    // Open properties panel for this field
    onSetSelectedField(field);
  };

  const handleGearClick = () => {
    onSetSelectedField(field);
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-field-container
      sx={{
        position: 'relative',
        border: '2px solid',
        borderColor: isDuplicate || isInactive || isRemovableChildField ? 'error.main' : isSelected ? 'primary.main' : neutral[200],
        borderRadius: 1,
        backgroundColor: isDuplicate || isInactive || isRemovableChildField
          ? 'rgba(211, 47, 47, 0.04)'
          : isSelected
            ? 'rgba(25, 118, 210, 0.04)'
            : 'background.paper',
        mx: 1,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: isDuplicate || isInactive || isRemovableChildField
            ? 'rgba(211, 47, 47, 0.08)'
            : isSelected
              ? 'rgba(25, 118, 210, 0.08)'
              : neutral[25],
          borderColor: isDuplicate || isInactive || isRemovableChildField ? 'error.main' : isSelected ? 'primary.main' : neutral[300],
        },
      }}
      onClick={handleFieldClick}
      title={
        isDuplicate
          ? 'Duplicate field ID'
          : isInactive
            ? 'This field is inactive'
            : isRemovableChildField
              ? 'Child-specific field in locked section - must be removed or moved before publishing'
              : undefined
      }
    >
      {/* Yellow banner for locked fields */}
      {isFieldLocked && (
        <Alert
          severity='warning'
          icon={<LockIcon fontSize='small' />}
          sx={{
            borderRadius: '4px 4px 0 0', // Round top corners only
            border: 'none',
            '& .MuiAlert-icon': {
              color: '#ed6c02',
            },
            '& .MuiAlert-message': {
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            },
          }}
        >
          {t(`performanceReporting.templates.builder.${templateMode}.lockedField.banner`)}
        </Alert>
      )}

      {/* Simplified Field Content - Label and Input */}
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Drag handle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: neutral[400],
              cursor: 'grab',
              p: 0.5,
              borderRadius: 0.5,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: neutral[100],
                color: neutral[600],
              },
              '&:active': {
                cursor: 'grabbing',
              },
            }}
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIndicatorIcon fontSize='small' />
          </Box>

          {/* Content Area - Label + Input */}
          <Box
            sx={{
              minWidth: 0,
              width: '100%',
            }}
          >
            {/* Field Label */}
            <Typography
              variant='body2'
              fontWeight='500'
              sx={{
                mb: 1,
                color: 'text.primary',
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'default',
              }}
            >
              {field?.effectiveLabel || 'Unknown Field'}
            </Typography>

            {/* Display Field Value */}
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  minHeight: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'background.paper',
                  minWidth: 0, // Critical for text truncation
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography
                  variant='body2'
                  title={
                    field?.effectiveDefaultValue || `Enter ${field?.effectiveLabel?.toLowerCase() || 'field label'}`
                  } // Tooltip on hover
                  sx={{
                    fontSize: '0.875rem',
                    color: field?.effectiveDefaultValue ? 'text.primary' : 'text.secondary',
                    fontStyle: field?.effectiveDefaultValue ? 'normal' : 'italic',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    cursor: 'default',
                  }}
                >
                  {formatFieldValue(field, fieldType)}
                </Typography>
              </Box>

              {/* Floating Action Buttons */}
              {isSelected && (
                <Box
                  data-field-options-panel
                  sx={{
                    position: 'absolute',
                    bottom: -45,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    px: 3,
                    py: 1.5,
                    minWidth: 120,
                    zIndex: 10,
                  }}
                >
                  {/* Gear button */}
                  <Tooltip
                    title='Field properties'
                    placement='top'
                  >
                    <IconButton
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGearClick();
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: neutral[400],
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                    >
                      <SettingsIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>

                  {/* Remove button */}
                  <Tooltip
                    title={
                      !canRemove
                        ? t('performanceReporting.templates.builder.lockedSection.cannotRemoveField')
                        : isRemovableChildField
                          ? 'Remove child-specific field'
                          : 'Remove field'
                    }
                    placement='top'
                  >
                    <span>
                      <IconButton
                        size='small'
                        disabled={!canRemove}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove();
                        }}
                        sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: neutral[400],
                          '&:hover': {
                            color: 'error.main',
                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          },
                          '&.Mui-disabled': {
                            color: neutral[300],
                          },
                        }}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SortableFieldItem;
